
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faFilterList } from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { deleteAgreement, getAgreements } from "../../api/Agreement";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";

const AgreementsPage = () => {
  const { tenantPublicId, agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [selectedAgreements, setSelectedAgreements] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [agreementsLoaded, setAgreementsLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const handleOpenModal = () => {
    if (selectedAgreements.length === 0) {
      toast.error("No agreements selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleExportToExcel = () => {
    const agreementsToExport =
      selectedAgreements.length > 0
        ? filteredAgreements.filter((agreement) =>
            selectedAgreements.includes(agreement.agreementPublicId)
          )
        : filteredAgreements;

    if (agreementsToExport.length === 0) {
      toast.error("No agreements selected");
      return;
    }

    const agreementsData = agreementsToExport.map(
      ({
        agreementPublicId,
        tenantPublicId,
        displayName,
        agreementPrincipalName,
        officeLocation,
        country,
        mobilePhone,
        managerDisplayName,
        lastSignInDateTime,
        OnPremisesSyncEnabled,
        accountTypeName,
        accountTypePrice,
        accountTypeStatus,
        billingGroupName,
        billingGroupStatus,
        createdBy,
        createdDateTimeUTC,
        updatedBy,
        updatedDateTimeUTC
      }) => ({
        "User Id": agreementPublicId,
        "Tenanant Id": tenantPublicId,
        "Name": displayName,
        "Username": agreementPrincipalName,
        "Office location": officeLocation,
        "Country": country,
        "Phone": mobilePhone,
        "Manager": managerDisplayName,
        "Last login": lastSignInDateTime,
        "On-prem sync": OnPremisesSyncEnabled,
        "Accout name": accountTypeName,
        "Account type price": accountTypePrice,
        "Account status": accountTypeStatus,
        "Billing group name": billingGroupName,
        "Billing group status": billingGroupStatus,
        "Createdb by": createdBy,
        "Created date": createdDateTimeUTC,
        "Updated by": updatedBy,
        "Updated date": updatedDateTimeUTC
      })
    );
    console.log("Exporting agreements data:", agreementsData);

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Agreements_${currentDate}`;

    handleExport({ Agreements: agreementsData }, filename);
  };

  useEffect(() => {
    handleGetAgreements();

    const handleAgreementUpdate = () => {
      console.log("🔄 Agreement update detected, reloading agreements...");
      handleGetAgreements();
    };

    window.addEventListener("agreementUpdated", handleAgreementUpdate);

    return () => {
      window.removeEventListener("agreementUpdated", handleAgreementUpdate);
    };
  }, [tenantPublicId]);

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = agreements;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = agreements.filter(
          (agreement) =>
            agreement.agreementPublicId?.toLowerCase().includes(searchLower) ||
            agreement.name?.toLowerCase().includes(searchLower) ||
            agreement.status?.toLowerCase().includes(searchLower) ||
            agreement.updatedBy?.toLowerCase().includes(searchLower) ||
            agreement.updatedDate?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredAgreements(filtered);
    };

    applyFilterAndSort();
  }, [search, agreements, sortColumn, sortOrder]);

  const handleGetAgreements = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAgreements = await getAgreements(accessToken);

      const formatted = newAgreements.map((agreement) => ({
        ...agreement,
        hasAlignmentMeeting: agreement.hasAlignmentMeeting ? "Yes" : "No",
        hasTacticalMeeting: agreement.hasTacticalMeeting ? "Yes" : "No",  
        hasOperationalMeeting: agreement.hasOperationalMeeting ? "Yes" : "No",
        hasStrategicMeeting: agreement.hasStrategicMeeting ? "Yes" : "No",
        officeBrach: agreement.officeBranch || "No",
    }));

      setAgreements(formatted);
      setFilteredAgreements(formatted);
      setAgreementsLoaded(true);
    } catch (error) {
      console.error(error);
      setAgreementsLoaded(true);
    }
  };

  /* const handleDeleteAgreement = async (agreementPublicId) => {
    const toastId = toast.loading("Deleting agreement...");
    try {
      const accessToken = await getAccessToken();
      await deleteAgreement(agreementPublicId, accessToken);
      const newAgreements = await getAgreements(accessToken);
      setAgreements(newAgreements);
      
      toast.success("Agreement deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

    } catch (error) {
      toast.error("Failed to delete agreement", { id: toastId });
      console.error(error);
    }
  }; */

  console.log("AGREEMENTS:", agreements);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Agreements"} loading={!agreementsLoaded} />
            </div>

            <div className={`${!agreementsLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Agreements</h2>

                <Link to="/agreement/create" className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create agreement
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn-outline-primary-small"
                    onClick={handleOpenModal}
                    hidden={selectedAgreements.length === 0}
                  >
                    Update billing group
                  </button>
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Status"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredAgreements}
                    selectedRows={selectedAgreements} // Map selectedAgreements to selectedRows
                    noCheckbox={true}
                    noEdit={false}
                    noDelete={true}
                    defaultRowsPerPage={500}
                    setSelectedRows={setSelectedAgreements} // Ensure selection updates correctly
                    columns={[
                      { key: "name", label: "Name" },
                      { key: "status", label: "Status" },
                      { key: "officeBrach", label: "Office branch" },
                      { key: "customerType", label: "Customer type" },
                      { key: "hasAlignmentMeeting", label: "Alignment meeting" },
                      { key: "hasTacticalMeeting", label: "Tactical meeting" },
                      { key: "hasOperationalMeeting", label: "Operational meeting" },
                      { key: "hasStrategicMeeting", label: "Strategic meeting" },
                      { key: "updatedBy", label: "Updated by" },
                      { key: "updatedDateTimeUTC", label: "Updated date", type: "date" },
                    ]}
                    primaryKey="agreementPublicId" // Match this with agreementPublicId
                    basePath={``}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default AgreementsPage;